import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderAuthorizeMailCheck from "./authorize-mail-check";

import { useStakeDebit } from "../../../CustomHooks/Queries";
import useStakeCheckingStatusAction from "../../../CustomHooks/useStakeCheckingStatusAction";

import { useModalContext } from "../../../Contexts/ModalContext";

import { formatCurrency } from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";

import "./mail-check.scss";

export default function renderCheckFeeEducation(modalContext, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalClassName, setModalTitle } = modalContext;

  updateModalComponent(() => <CheckFeeEducation {...props} />);
  pushModalStack(() => renderCheckFeeEducation(modalContext, props));
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("mail-check-modal check-fee-modal align-title-left hide-back-button");
  setModalTitle(props?.freeCheckEligible ? "You’re eligible for a free check" : "Avoid this check fee");
}

const ALLOWANCE_WORDS = { 1: "one", 2: "two", 3: "three", 4: "four", 5: "five" };
const formatAllowanceWord = (allowance) => ALLOWANCE_WORDS[allowance] ?? String(allowance);

// e.g. 12 -> "per year", 6 -> "every 6 months", 1 -> "per month"
const formatCooldownPeriodPhrase = (cooldownMonths) => {
  if (!cooldownMonths) return "";
  if (cooldownMonths === 12) return "per year";
  if (cooldownMonths === 1) return "per month";
  if (cooldownMonths % 12 === 0) return `every ${cooldownMonths / 12} years`;
  return `every ${cooldownMonths} months`;
};

const formatFreeCheckLabel = (allowance, cooldownMonths) => {
  const checkNoun = allowance === 1 ? "free check" : "free checks";
  const period = formatCooldownPeriodPhrase(cooldownMonths);
  return `${formatAllowanceWord(allowance)} ${checkNoun}${period ? ` ${period}` : ""}`;
};

const formatEducationBodyCopy = (isBankAllowed, isVenmoAllowed) => {
  if (isBankAllowed && isVenmoAllowed) {
    return "Choose a free transfer to your bank account or Venmo, or open a free Stake Checking account.";
  }
  if (isBankAllowed) {
    return "Choose a free transfer to your bank account, or open a free Stake Checking account.";
  }
  if (isVenmoAllowed) {
    return "Choose a free transfer to Venmo, or open a free Stake Checking account.";
  }
  return "Open a free Stake Checking account to transfer your balance.";
};

const CheckFeeEducation = ({
  cashAmount,
  mailingAddress,
  checkFee,
  freeCheckEligible,
  freeCheckAllowance,
  freeCheckCooldownMonths,
  isBankAllowed,
  isVenmoAllowed,
}) => {
  const modalContext = useModalContext();

  const { data: stakeDebit } = useStakeDebit();
  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const onContinue = () =>
    renderAuthorizeMailCheck(modalContext, {
      cashAmount,
      mailingAddress,
      checkFee,
      isFeeCharged: !freeCheckEligible,
    });

  const freeCheckLabel = formatFreeCheckLabel(freeCheckAllowance, freeCheckCooldownMonths);
  const freeCheckCalloutLabel = freeCheckLabel.charAt(0).toUpperCase() + freeCheckLabel.slice(1);
  const educationBodyCopy = formatEducationBodyCopy(isBankAllowed, isVenmoAllowed);

  return (
    <div className="modal-dialog-content cfe-content">
      {freeCheckEligible ? (
        <div className="cfe-callout cfe-callout-free">
          <div className="cfe-callout-icon">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.563 8L6.188 11.625 13.438 4.375"
                stroke="#54FF99"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="cfe-callout-info">
            <span className="cfe-callout-label">{freeCheckCalloutLabel}</span>
            <span className="cfe-callout-value">Free</span>
          </div>
        </div>
      ) : (
        <div className="cfe-callout cfe-callout-fee">
          <div className="cfe-callout-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="7.25" fill="none" stroke="#FFB020" strokeWidth="1.5" />
              <path d="M9 5.5V9.5" stroke="#FFB020" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="9" cy="12.25" r="0.9" fill="#FFB020" />
            </svg>
          </div>
          <div className="cfe-callout-info">
            <span className="cfe-callout-label">Check delivery fee</span>
            <span className="cfe-callout-value">{formatCurrency(checkFee)}</span>
          </div>
        </div>
      )}

      <div className="cfe-copy">
        <h3 className="cfe-heading">{freeCheckEligible ? "Avoid fees on future checks" : "Don’t want to pay this fee?"}</h3>
        <p className="cfe-body">{educationBodyCopy}</p>
      </div>

      <div className="cfe-actions">
        <PrimaryButton className="cfe-primary-btn" onClick={() => stakeCheckingStatusAction(stakeDebit?.data)}>
          Open Stake Checking
        </PrimaryButton>
        <button type="button" className="cfe-secondary-btn" onClick={onContinue}>
          {freeCheckEligible
            ? "Continue with the free mailed check"
            : `Continue with the mailed check - ${formatCurrency(checkFee)} fee`}
        </button>
      </div>

      {freeCheckEligible && (
        <p className="cfe-disclaimer">*Venmo and/or your bank may add fees for instant transfers.</p>
      )}
    </div>
  );
};
